import { OFFERED_CHAT_PROVIDER_IDS, SUGGESTION_MODES, type ProviderId, type SuggestionMode } from '@rp/contracts'
import { Button, Card, Choice, Field } from '../../design'
import { SETTINGS_COPY } from './copy'
import { ReplyClaimField } from './ReplyClaimField'
import { ThresholdScale } from './ThresholdScale'
import { withDistinctProviders, type EditableSettings } from './queries'

type TenantSettingsFormProps = {
  draft: EditableSettings
  choices: readonly ProviderId[]
  onChange: (draft: EditableSettings) => void
  onSave: () => void
  saving: boolean
  saved: boolean
}

const OFFERED: readonly ProviderId[] = OFFERED_CHAT_PROVIDER_IDS

const isOffered = (provider: ProviderId): boolean => OFFERED.includes(provider)

const ProviderOptions = (choices: readonly ProviderId[], current: ProviderId | null, excluded: ProviderId | null) => {
  const sold = choices.filter(isOffered)
  const offered = current === null || sold.includes(current) ? sold : [current, ...sold]
  return offered
    .filter((provider) => provider !== excluded)
    .map((provider) => (
      <option key={provider} value={provider}>
        {provider}
      </option>
    ))
}

export const TenantSettingsForm = ({ draft, choices, onChange, onSave, saving, saved }: TenantSettingsFormProps) => (
  <Card as="section" className="settings-card">
    <h2>{SETTINGS_COPY.editable}</h2>

    <Field id="settings-name" label={SETTINGS_COPY.name}>
      {({ id }) => <input id={id} value={draft.name} onChange={(event) => onChange({ ...draft, name: event.target.value })} />}
    </Field>

    <fieldset className="settings-modes">
      <legend className="field-label">{SETTINGS_COPY.suggestionMode}</legend>
      {SUGGESTION_MODES.map((mode) => (
        <Choice
          key={mode}
          name="settings-mode"
          value={mode}
          checked={draft.suggestionMode === mode}
          onChange={(value) => onChange({ ...draft, suggestionMode: value as SuggestionMode })}
          label={SETTINGS_COPY.suggestionModes[mode]}
          description={SETTINGS_COPY.suggestionModeHints[mode]}
        />
      ))}
    </fieldset>

    <div className="settings-pair">
      <Field id="settings-primary" label={SETTINGS_COPY.primaryProvider}>
        {({ id }) => (
          <select
            id={id}
            value={draft.primaryProvider}
            onChange={(event) =>
              onChange(withDistinctProviders({ ...draft, primaryProvider: event.target.value as ProviderId }))
            }
          >
            {ProviderOptions(choices, draft.primaryProvider, null)}
          </select>
        )}
      </Field>
      <Field id="settings-secondary" label={SETTINGS_COPY.secondaryProvider}>
        {({ id }) => (
          <select
            id={id}
            value={draft.secondaryProvider ?? ''}
            onChange={(event) =>
              onChange({ ...draft, secondaryProvider: event.target.value === '' ? null : (event.target.value as ProviderId) })
            }
          >
            <option value="">{SETTINGS_COPY.secondaryNone}</option>
            {ProviderOptions(choices, draft.secondaryProvider, draft.primaryProvider)}
          </select>
        )}
      </Field>
    </div>

    <Field id="settings-claim" label={SETTINGS_COPY.claimIdleMinutes} hint={SETTINGS_COPY.claimIdleHint}>
      {({ id, describedBy }) => (
        <input
          id={id}
          className="settings-number"
          aria-describedby={describedBy}
          type="number"
          min={1}
          max={120}
          value={draft.claimIdleMinutes}
          onChange={(event) => onChange({ ...draft, claimIdleMinutes: Number(event.target.value) })}
        />
      )}
    </Field>

    <ReplyClaimField
      requiresClaim={draft.replyRequiresClaim}
      onChange={(replyRequiresClaim) => onChange({ ...draft, replyRequiresClaim })}
    />

    <Field id="settings-threshold" label={SETTINGS_COPY.answerabilityThreshold} hint={SETTINGS_COPY.answerabilityHint}>
      {({ id, describedBy }) => (
        <div className="settings-threshold">
          <input
            id={id}
            className="settings-number"
            aria-describedby={describedBy}
            type="number"
            min={0}
            max={1}
            step={0.05}
            value={draft.answerabilityThreshold}
            onChange={(event) => onChange({ ...draft, answerabilityThreshold: Number(event.target.value) })}
          />
          <ThresholdScale value={draft.answerabilityThreshold} />
        </div>
      )}
    </Field>

    <div className="settings-save">
      <Button variant="primary" size="large" pending={saving} pendingLabel={SETTINGS_COPY.saving} onClick={onSave}>
        {SETTINGS_COPY.save}
      </Button>
      {saved ? (
        <p className="settings-saved text-tone tone-success" role="status">
          {SETTINGS_COPY.saved}
        </p>
      ) : null}
    </div>
  </Card>
)
